import { useState } from 'react';
import { Braces, Copy, Check, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { useToolUsage } from '@/hooks/useToolUsage';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const JsonFormatter = () => {
  useToolUsage('json_formatter'); // Track tool usage

  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [copied, setCopied] = useState(false);

  const formatJson = () => {
    try {
      const parsed = JSON.parse(input);
      const formatted = JSON.stringify(parsed, null, 2);
      setOutput(formatted);
      setError(null);
      setIsValid(true);
      toast.success('JSON formatted successfully!');
    } catch (err: any) {
      setError(err.message);
      setIsValid(false);
      setOutput('');
      toast.error('Invalid JSON');
    }
  };

  const validateJson = () => {
    try {
      JSON.parse(input);
      setError(null);
      setIsValid(true);
      toast.success('JSON is valid!');
    } catch (err: any) {
      setError(err.message);
      setIsValid(false);
      toast.error('Invalid JSON');
    }
  };

  const minifyJson = () => {
    try {
      const parsed = JSON.parse(input);
      const minified = JSON.stringify(parsed);
      setOutput(minified);
      setError(null);
      setIsValid(true);
      toast.success('JSON minified!');
    } catch (err: any) {
      setError(err.message);
      setIsValid(false);
      setOutput('');
      toast.error('Invalid JSON');
    }
  };

  const copyOutput = () => {
    if (output) {
      navigator.clipboard.writeText(output);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError(null);
    setIsValid(null);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <Braces className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">JSON Formatter</h1>
          <p className="text-muted-foreground">
            Format, validate, and minify JSON data
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Input */}
        <Card className="glass border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Input JSON</span>
              {isValid !== null && (
                <div className={cn(
                  "flex items-center gap-1 text-sm",
                  isValid ? "text-green-500" : "text-destructive"
                )}>
                  {isValid ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    <AlertCircle className="h-4 w-4" />
                  )}
                  {isValid ? 'Valid' : 'Invalid'}
                </div>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder='Paste your JSON here...\n{\n  "example": "data"\n}'
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="min-h-[300px] font-mono text-sm resize-none"
            />
            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                <p className="font-medium">Error:</p>
                <p className="font-mono">{error}</p>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button onClick={formatJson} disabled={!input.trim()}>
                Format
              </Button>
              <Button variant="outline" onClick={validateJson} disabled={!input.trim()}>
                Validate
              </Button>
              <Button variant="outline" onClick={minifyJson} disabled={!input.trim()}>
                Minify
              </Button>
              <Button variant="ghost" onClick={clearAll}>
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Output */}
        <Card className="glass border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Output</span>
              {output && (
                <Button variant="ghost" size="sm" onClick={copyOutput} className="gap-2">
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  Copy
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Formatted JSON will appear here..."
              value={output}
              readOnly
              className="min-h-[300px] font-mono text-sm resize-none bg-muted/30"
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default JsonFormatter;